import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { HedgeAnalysis } from '@/lib/hedgeAnalysis';
import { cn } from '@/lib/utils';

type StrategyType = 'put' | 'collar' | 'covered';

interface Props {
  analysis: HedgeAnalysis;
  strategy: StrategyType;
  putIndex?: number; // which protective put (0=ATM, 1=5%OTM, 2=10%OTM)
}

function fmt(v: number): string {
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function computePnL(
  analysis: HedgeAnalysis,
  strategy: StrategyType,
  pricePct: number, // -100 to +100
  putIndex: number
): { stockPnL: number; strategyPnL: number; netPnL: number; newPrice: number; premiumCost: number } {
  const pos = analysis.position;
  const spot = pos.currentPrice;
  const qty = pos.quantity;
  const newPrice = spot * (1 + pricePct / 100);
  const stockPnL = (newPrice - spot) * qty;

  if (strategy === 'put') {
    const put = analysis.protectivePuts[putIndex] || analysis.protectivePuts[0];
    const contracts = put.contractsNeeded;
    const strike = put.strikePrice;
    const premiumCost = put.totalCost;
    // Put payoff at expiry
    const putPayoff = Math.max(strike - newPrice, 0) * contracts * 100;
    const strategyPnL = putPayoff - premiumCost;
    return { stockPnL, strategyPnL, netPnL: stockPnL + strategyPnL, newPrice, premiumCost };
  }

  if (strategy === 'collar') {
    const c = analysis.collar;
    const contracts = c.contractsNeeded;
    const putPayoff = Math.max(c.putStrike - newPrice, 0) * contracts * 100;
    const callPayoff = -Math.max(newPrice - c.callStrike, 0) * contracts * 100; // short call
    const premiumCost = c.netPremium > 0 ? c.netPremium : 0;
    const premiumCredit = c.netPremium < 0 ? -c.netPremium : 0;
    const strategyPnL = putPayoff + callPayoff - premiumCost + premiumCredit;
    return { stockPnL, strategyPnL, netPnL: stockPnL + strategyPnL, newPrice, premiumCost: Math.abs(c.netPremium) };
  }

  // covered call
  const cc = analysis.coveredCall;
  const contracts = cc.contractsNeeded;
  const premiumCost = cc.incomeGenerated;
  const callPayoff = -Math.max(newPrice - cc.strikePrice, 0) * contracts * 100;
  const strategyPnL = premiumCost + callPayoff;
  return { stockPnL, strategyPnL, netPnL: stockPnL + strategyPnL, newPrice, premiumCost };
}

const STRATEGY_LABELS: Record<StrategyType, string> = {
  put: 'Protective Put',
  collar: 'Collar',
  covered: 'Covered Call',
};

export function StrategyPnLChart({ analysis, strategy, putIndex = 1 }: Props) {
  const [sliderValue, setSliderValue] = useState([0]);
  const pricePct = sliderValue[0];
  const pos = analysis.position;
  const spot = pos.currentPrice;

  const currentCalc = useMemo(
    () => computePnL(analysis, strategy, pricePct, putIndex),
    [analysis, strategy, pricePct, putIndex]
  );

  // Generate chart data from -100 to +100 in steps of 2
  const chartData = useMemo(() => {
    const data = [];
    for (let pct = -100; pct <= 100; pct += 2) {
      const calc = computePnL(analysis, strategy, pct, putIndex);
      const net = Math.round(calc.netPnL);
      data.push({
        pct,
        price: Math.round(calc.newPrice * 100) / 100,
        stockOnly: Math.round(calc.stockPnL),
        withStrategy: net,
        profitZone: net >= 0 ? net : 0,
        lossZone: net < 0 ? net : 0,
        strategyPnL: Math.round(calc.strategyPnL),
      });
    }
    return data;
  }, [analysis, strategy, putIndex]);

  // Find break-even point (where netPnL crosses zero)
  const breakEvenPct = useMemo(() => {
    for (let pct = -100; pct <= 100; pct += 1) {
      const a = computePnL(analysis, strategy, pct, putIndex).netPnL;
      const b = computePnL(analysis, strategy, pct + 1, putIndex).netPnL;
      if ((a <= 0 && b >= 0) || (a >= 0 && b <= 0)) {
        // Linear interpolation
        if (b === a) return pct;
        return Math.round(pct + (-a / (b - a)));
      }
    }
    return null;
  }, [analysis, strategy, putIndex]);

  const breakEvenPrice = breakEvenPct !== null ? spot * (1 + breakEvenPct / 100) : null;

  const isProfit = currentCalc.netPnL >= 0;

  return (
    <Card className="mt-4 border-2 border-primary/20 bg-gradient-to-br from-background to-muted/30">
      <CardContent className="pt-5 space-y-4">
        {/* Header with strategy label */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs font-bold bg-primary/10 text-primary border-primary/30">
              {STRATEGY_LABELS[strategy]} P&L Simulator
            </Badge>
            <span className="text-xs text-muted-foreground">{pos.ticker}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            Premium Cost: <span className="font-bold text-destructive">${fmt(currentCalc.premiumCost)}</span>
          </div>
        </div>

        {/* P&L Chart */}
        <div className="h-[240px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`profitGrad-${pos.ticker}-${strategy}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(142 71% 45%)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(142 71% 45%)" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id={`lossGrad-${pos.ticker}-${strategy}`} x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="hsl(0 84% 60%)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(0 84% 60%)" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id={`stockGrad-${pos.ticker}-${strategy}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
              <XAxis
                dataKey="pct"
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(v) => `${v}%`}
                interval={9}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs space-y-1">
                      <p className="font-bold text-foreground">Price: ${fmt(d.price)} ({d.pct >= 0 ? '+' : ''}{d.pct}%)</p>
                      <p className="text-muted-foreground">Stock Only: <span className={d.stockOnly >= 0 ? 'text-green-500' : 'text-red-500'}>${fmt(d.stockOnly)}</span></p>
                      <p className="font-bold">With {STRATEGY_LABELS[strategy]}: <span className={d.withStrategy >= 0 ? 'text-green-500' : 'text-red-500'}>${fmt(d.withStrategy)}</span></p>
                      <p className="text-muted-foreground">Strategy P&L: <span className={d.strategyPnL >= 0 ? 'text-green-500' : 'text-red-500'}>${fmt(d.strategyPnL)}</span></p>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={0} stroke="hsl(var(--foreground))" strokeWidth={1.5} strokeDasharray="4 4" />
              <ReferenceLine
                x={pricePct}
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                strokeDasharray="3 3"
                label={{ value: `${pricePct >= 0 ? '+' : ''}${pricePct}%`, position: 'top', fontSize: 11, fill: 'hsl(var(--primary))' }}
              />
              {breakEvenPct !== null && (
                <ReferenceLine
                  x={breakEvenPct}
                  stroke="hsl(var(--chart-4))"
                  strokeWidth={2.5}
                  strokeDasharray="6 3"
                  label={{
                    value: `BE: $${breakEvenPrice ? fmt(breakEvenPrice) : '?'} (${breakEvenPct >= 0 ? '+' : ''}${breakEvenPct}%)`,
                    position: 'insideTopRight',
                    fontSize: 11,
                    fontWeight: 700,
                    fill: 'hsl(var(--chart-4))',
                  }}
                />
              )}
              <Area
                type="monotone"
                dataKey="stockOnly"
                stroke="hsl(var(--muted-foreground))"
                strokeWidth={1.5}
                strokeDasharray="5 5"
                fill={`url(#stockGrad-${pos.ticker}-${strategy})`}
                name="Stock Only"
              />
              <Area
                type="monotone"
                dataKey="profitZone"
                stroke="hsl(142 71% 45%)"
                strokeWidth={0}
                fill={`url(#profitGrad-${pos.ticker}-${strategy})`}
                name="Profit Zone"
                legendType="none"
              />
              <Area
                type="monotone"
                dataKey="lossZone"
                stroke="hsl(0 84% 60%)"
                strokeWidth={0}
                fill={`url(#lossGrad-${pos.ticker}-${strategy})`}
                name="Loss Zone"
                legendType="none"
              />
              <Area
                type="monotone"
                dataKey="withStrategy"
                stroke="hsl(var(--chart-2))"
                strokeWidth={2.5}
                fill="transparent"
                name={`With ${STRATEGY_LABELS[strategy]}`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-5 text-xs flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-[2px] border-t-2 border-dashed" style={{ borderColor: 'hsl(var(--muted-foreground))' }} />
            <span className="text-muted-foreground">Stock Only</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-[3px] rounded" style={{ background: 'hsl(var(--chart-2))' }} />
            <span className="text-foreground font-medium">With {STRATEGY_LABELS[strategy]}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 rounded-sm" style={{ background: 'hsla(142, 71%, 45%, 0.25)' }} />
            <span className="text-green-600 dark:text-green-400 font-medium">Profit</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 rounded-sm" style={{ background: 'hsla(0, 84%, 60%, 0.25)' }} />
            <span className="text-red-600 dark:text-red-400 font-medium">Loss</span>
          </div>
          {breakEvenPct !== null && (
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-[2px] border-t-2 border-dashed" style={{ borderColor: 'hsl(var(--chart-4))' }} />
              <span className="font-semibold" style={{ color: 'hsl(var(--chart-4))' }}>Break-Even: ${breakEvenPrice ? fmt(breakEvenPrice) : '?'}</span>
            </div>
          )}
        </div>

        {/* Slider */}
        <div className="space-y-2 px-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Stock = $0 (-100%)</span>
            <span className="font-bold text-foreground text-sm">
              Stock Price Move: {pricePct >= 0 ? '+' : ''}{pricePct}%
            </span>
            <span>Stock = ${fmt(spot * 2)} (+100%)</span>
          </div>
          <Slider
            value={sliderValue}
            onValueChange={setSliderValue}
            min={-100}
            max={100}
            step={1}
            className="w-full"
          />
        </div>

        {/* Dynamic P&L Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">New Price</p>
            <p className="text-lg font-bold text-foreground">${fmt(currentCalc.newPrice)}</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Stock P&L</p>
            <p className={cn("text-lg font-bold", currentCalc.stockPnL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
              ${fmt(currentCalc.stockPnL)}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Strategy P&L</p>
            <p className={cn("text-lg font-bold", currentCalc.strategyPnL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
              ${fmt(currentCalc.strategyPnL)}
            </p>
          </div>
          <div className={cn("p-3 rounded-lg text-center border-2", isProfit ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10')}>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Net P&L</p>
            <p className={cn("text-lg font-bold", isProfit ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
              ${fmt(currentCalc.netPnL)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
